import { Link } from 'react-router-dom';

function Placeholder({ children }: { children: string }) {
  return (
    <span className="bg-yellow-100 text-yellow-800 px-1 rounded font-mono text-sm">
      [{children}]
    </span>
  );
}

export function CookiePolicyPage() {
  return (
    <div className="min-h-screen bg-cream-100">
      <header className="bg-white border-b border-cream-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="font-fraunces text-2xl font-light tracking-tight text-navy-mid">
            <em className="font-medium text-primary-500" style={{ fontStyle: 'italic' }}>Kin</em>Care
          </Link>
          <Link to="/" className="text-sm text-primary-600 hover:text-primary-700">
            Back to home
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Cookie Policy</h1>
        <p className="text-sm text-gray-500 mb-10">
          Last updated: <Placeholder>DATE</Placeholder>
        </p>

        <section className="space-y-4 text-gray-700 leading-relaxed">
          <h2 className="text-xl font-semibold text-gray-900 mt-10 mb-4">
            1. What Are Cookies
          </h2>
          <p>
            Cookies are small text files placed on your device when you visit a website. They are
            widely used to make websites work efficiently and to provide information to the owners
            of the site. We use cookies and similar technologies to ensure our platform functions
            correctly and to improve your experience.
          </p>

          <h2 className="text-xl font-semibold text-gray-900 mt-10 mb-4">
            2. How We Use Cookies
          </h2>
          <p>We use cookies for the following purposes:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Essential cookies:</strong> Required for the platform to function. These
              include authentication session cookies and security tokens.
            </li>
            <li>
              <strong>Preference cookies:</strong> Remember your settings and preferences, such as
              cookie consent choices.
            </li>
            <li>
              <strong>Analytics cookies:</strong>{' '}
              <Placeholder>DETAIL IF/WHICH ANALYTICS ARE USED</Placeholder> Help us understand how
              visitors interact with our platform so we can improve it.
            </li>
          </ul>

          <h2 className="text-xl font-semibold text-gray-900 mt-10 mb-4">
            3. Cookies We Use
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-gray-300">
                  <th className="text-left py-3 pr-4 font-semibold text-gray-900">Name</th>
                  <th className="text-left py-3 pr-4 font-semibold text-gray-900">Purpose</th>
                  <th className="text-left py-3 pr-4 font-semibold text-gray-900">Duration</th>
                  <th className="text-left py-3 font-semibold text-gray-900">Type</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-200">
                  <td className="py-3 pr-4 font-mono text-xs">__clerk_*</td>
                  <td className="py-3 pr-4">Authentication session management</td>
                  <td className="py-3 pr-4">Session / 1 year</td>
                  <td className="py-3">Essential</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-3 pr-4 font-mono text-xs">kin-cookie-consent</td>
                  <td className="py-3 pr-4">Stores your cookie preference</td>
                  <td className="py-3 pr-4">Persistent</td>
                  <td className="py-3">Essential</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-3 pr-4 font-mono text-xs">
                    <Placeholder>COOKIE NAME</Placeholder>
                  </td>
                  <td className="py-3 pr-4">
                    <Placeholder>PURPOSE</Placeholder>
                  </td>
                  <td className="py-3 pr-4">
                    <Placeholder>DURATION</Placeholder>
                  </td>
                  <td className="py-3">
                    <Placeholder>TYPE</Placeholder>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <h2 className="text-xl font-semibold text-gray-900 mt-10 mb-4">
            4. Managing Cookies
          </h2>
          <p>
            You can control and manage cookies in several ways:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Our cookie consent tool:</strong> When you first visit our site, you can
              choose to accept or reject non-essential cookies using our consent banner.
            </li>
            <li>
              <strong>Browser settings:</strong> Most browsers allow you to refuse or delete
              cookies through their settings. Note that disabling essential cookies may prevent
              the platform from functioning correctly.
            </li>
          </ul>

          <h2 className="text-xl font-semibold text-gray-900 mt-10 mb-4">
            5. Third-Party Cookies
          </h2>
          <p>
            Our platform uses the following third-party services that may set cookies:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Clerk</strong> (authentication): Sets cookies to manage secure user sessions.
              See{' '}
              <a
                href="https://clerk.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:text-primary-700 underline"
              >
                Clerk&apos;s Privacy Policy
              </a>
              .
            </li>
            <li>
              <Placeholder>OTHER THIRD-PARTY SERVICES AND THEIR COOKIE POLICIES</Placeholder>
            </li>
          </ul>

          <h2 className="text-xl font-semibold text-gray-900 mt-10 mb-4">
            6. Changes to This Policy
          </h2>
          <p>
            We may update this Cookie Policy from time to time. Any changes will be posted on this
            page with an updated revision date.
          </p>

          <h2 className="text-xl font-semibold text-gray-900 mt-10 mb-4">7. Contact Us</h2>
          <p>
            If you have questions about our use of cookies, please contact us at{' '}
            <Placeholder>PRIVACY EMAIL ADDRESS</Placeholder>.
          </p>
        </section>
      </main>

      <footer className="border-t border-cream-200 mt-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-wrap gap-6 text-sm text-gray-500">
          <Link to="/privacy" className="hover:text-gray-700">
            Privacy Policy
          </Link>
          <Link to="/cookies" className="hover:text-gray-700">
            Cookie Policy
          </Link>
          <Link to="/terms" className="hover:text-gray-700">
            Terms of Service
          </Link>
        </div>
      </footer>
    </div>
  );
}
